import * as React from "react";

export interface IElementBand {
  name: string;
  kuikaId: string;
  top: number;
  height: number;
  color: string;
}

export interface IElementBandsBarProps {
  height: number;
  zoomScale: number;
  pageBreakPositions?: number[];
  selectedKuikaIds?: string[];
  onSelectElement?: (kuikaId: string) => void;
}

interface IElementBandsBarState {
  bands: IElementBand[];
}

const BAND_COLORS: Record<string, string> = {
  Header: "var(--blue-7)",
  Footer: "var(--purple-7)",
  "Page Header": "var(--blue-5)",
  "Page Footer": "var(--purple-5)",
  ReportTable: "var(--orange-5)",
  StaticTable: "var(--green-5)"
};

const TRACKED_COMPONENTS = ["Header", "Footer", "Page Header", "Page Footer", "ReportTable", "StaticTable"];

export class ElementBandsBar extends React.Component<IElementBandsBarProps, IElementBandsBarState> {
  private pollTimer: number | null = null;

  private scrollListener: (() => void) | null = null;

  private scrollAttachTimer: number | null = null;

  private resizeObserver: ResizeObserver | null = null;

  private mutationObserver: MutationObserver | null = null;

  private recalcRAF: number | null = null;

  private observedRootDI: HTMLElement | null = null;

  constructor(props: IElementBandsBarProps) {
    super(props);
    this.state = { bands: [] };
  }

  componentDidMount() {
    this.startPolling();
    this.attachScrollListener();
  }

  componentWillUnmount() {
    this.detachObservers();
    if (this.scrollAttachTimer) {
      window.clearInterval(this.scrollAttachTimer);
      this.scrollAttachTimer = null;
    }
    if (this.recalcRAF) {
      cancelAnimationFrame(this.recalcRAF);
      this.recalcRAF = null;
    }
    this.detachScrollListener();
  }

  shouldComponentUpdate(nextProps: IElementBandsBarProps, nextState: IElementBandsBarState): boolean {
    if (nextProps.height !== this.props.height || nextProps.zoomScale !== this.props.zoomScale) return true;
    if (nextState.bands.length !== this.state.bands.length) return true;
    const prevBreaks = this.props.pageBreakPositions;
    const nextBreaks = nextProps.pageBreakPositions;
    if (prevBreaks !== nextBreaks) {
      if (!prevBreaks || !nextBreaks || prevBreaks.length !== nextBreaks.length) return true;
      for (let i = 0; i < prevBreaks.length; i++) {
        if (prevBreaks[i] !== nextBreaks[i]) return true;
      }
    }
    const prevSelected = this.props.selectedKuikaIds;
    const nextSelected = nextProps.selectedKuikaIds;
    if (prevSelected !== nextSelected) {
      if (!prevSelected || !nextSelected || prevSelected.length !== nextSelected.length) return true;
      for (let i = 0; i < prevSelected.length; i++) {
        if (prevSelected[i] !== nextSelected[i]) return true;
      }
    }
    for (let i = 0; i < nextState.bands.length; i++) {
      const a = nextState.bands[i];
      const b = this.state.bands[i];
      if (a.name !== b.name || a.top !== b.top || a.height !== b.height) return true;
    }
    return false;
  }

  startPolling = () => {
    const tryAttach = () => {
      const doc = this.getIframeDoc();
      if (!doc) return;

      const rootDI = doc.getElementById("rootDI");
      if (!rootDI) {
        // rootDI disappeared (iframe reloading) – clear stale observers
        if (this.observedRootDI) {
          if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
          }
          if (this.mutationObserver) {
            this.mutationObserver.disconnect();
            this.mutationObserver = null;
          }
          this.observedRootDI = null;
        }
        return;
      }

      // Re-attach observers when rootDI is new or replaced (e.g. iframe re-render)
      if (rootDI !== this.observedRootDI) {
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
          this.resizeObserver = null;
        }
        if (this.mutationObserver) {
          this.mutationObserver.disconnect();
          this.mutationObserver = null;
        }
        this.observedRootDI = rootDI;
        this.attachObservers(doc, rootDI);
      }

      this.recalcBands();
    };

    tryAttach();
    if (!this.pollTimer) {
      this.pollTimer = window.setInterval(tryAttach, 1000);
    }
  };

  getIframeDoc = (): Document | null => {
    const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement | null;
    return iframe?.contentDocument || iframe?.contentWindow?.document || null;
  };

  getActualScrollOffset = (): number => {
    const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
    const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
    const reportWrapper = doc?.getElementsByClassName("kuika_rootDIWrapper")[0] as HTMLElement;

    if (reportWrapper) {
      return reportWrapper.scrollTop || 0;
    }

    return 0;
  };

  attachScrollListener = () => {
    const tryAttach = () => {
      const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
      const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
      const reportWrapper = doc?.getElementsByClassName("kuika_rootDIWrapper")[0] as HTMLElement;

      if (reportWrapper && !this.scrollListener) {
        this.scrollListener = () => {
          this.forceUpdate();
        };
        reportWrapper.addEventListener("scroll", this.scrollListener, { passive: true });
      }
    };

    tryAttach();
    if (!this.scrollAttachTimer) {
      this.scrollAttachTimer = window.setInterval(tryAttach, 500);
    }
  };

  detachScrollListener = () => {
    if (this.scrollListener) {
      const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
      const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
      const reportWrapper = doc?.getElementsByClassName("kuika_rootDIWrapper")[0] as HTMLElement;

      if (reportWrapper) {
        reportWrapper.removeEventListener("scroll", this.scrollListener);
      }
      this.scrollListener = null;
    }
  };

  scheduleRecalc = () => {
    if (this.recalcRAF) {
      cancelAnimationFrame(this.recalcRAF);
    }
    this.recalcRAF = requestAnimationFrame(() => {
      this.recalcRAF = null;
      this.recalcBands();
    });
  };

  attachObservers = (doc: Document, rootDI: HTMLElement) => {
    // Observe DOM mutations in rootDI to detect element additions/removals/resizes
    if (!this.mutationObserver) {
      this.mutationObserver = new MutationObserver(() => this.scheduleRecalc());
      this.mutationObserver.observe(rootDI, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["style"]
      });
    }

    // Observe size changes on tracked elements
    this.attachResizeObserver(doc);
  };

  attachResizeObserver = (doc: Document) => {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    this.resizeObserver = new ResizeObserver(() => this.scheduleRecalc());

    for (const compName of TRACKED_COMPONENTS) {
      const selector = `[kuikacomponentname="${compName}"]`;
      const elements = doc.querySelectorAll(selector);
      elements.forEach((el) => this.resizeObserver!.observe(el));
    }
  };

  detachObservers = () => {
    if (this.pollTimer) {
      window.clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
    this.observedRootDI = null;
  };

  private static cleanKuikaId(raw: string): string {
    return raw.replace(/-lastChildItem$/, "");
  }

  recalcBands = () => {
    const doc = this.getIframeDoc();
    if (!doc) return;

    const rootDI = doc.getElementById("rootDI");
    if (!rootDI) return;

    const rootRect = rootDI.getBoundingClientRect();
    const bands: IElementBand[] = [];
    const seenKuikaIds = new Set<string>();

    for (const compName of TRACKED_COMPONENTS) {
      const selector = `[kuikacomponentname="${compName}"]`;
      const elements = doc.querySelectorAll(selector);
      elements.forEach((el) => {
        const rawKuikaId = el.getAttribute("kuikaid") || "";
        const kuikaId = ElementBandsBar.cleanKuikaId(rawKuikaId);

        // Deduplicate: wrapper div and inner element both match for the same component
        if (kuikaId && seenKuikaIds.has(kuikaId)) return;

        // For StaticTable/ReportTable, use the inner <table> element for accurate dimensions
        const measureEl =
          compName === "StaticTable" || compName === "ReportTable" ? el.querySelector("table") || el : el;
        const rect = measureEl.getBoundingClientRect();
        // Position relative to rootDI (scroll-independent)
        const top = rect.top - rootRect.top;
        const height = rect.height;
        if (height > 0) {
          if (kuikaId) seenKuikaIds.add(kuikaId);
          bands.push({
            name: compName,
            kuikaId,
            top,
            height,
            color: BAND_COLORS[compName] || "var(--dark-6)"
          });
        }
      });
    }

    // Sort by top position
    bands.sort((a, b) => a.top - b.top);

    // Re-attach resize observer if new elements appeared
    this.attachResizeObserver(doc);

    this.setState({ bands });
  };

  handleBandClick = (e: React.MouseEvent, band: IElementBand) => {
    e.stopPropagation();
    if (band.kuikaId && this.props.onSelectElement) {
      this.props.onSelectElement(band.kuikaId);
    }
  };

  renderBand = (band: IElementBand, index: number, scaledTop: number, scaledHeight: number, isSelected: boolean) => {
    const showLabel = scaledHeight >= 20;
    const isClickable = !!band.kuikaId && !!this.props.onSelectElement;

    return (
      <div
        key={`${band.name}-${index}`}
        className="element-band"
        onClick={isClickable ? (e) => this.handleBandClick(e, band) : undefined}
        style={{
          position: "absolute",
          top: `${scaledTop}px`,
          left: 0,
          width: "100%",
          height: `${scaledHeight}px`,
          boxSizing: "border-box",
          pointerEvents: isClickable ? "auto" : "none",
          overflow: "hidden",
          cursor: isClickable ? "pointer" : "default"
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundColor: band.color,
            opacity: isSelected ? 0.35 : 0.12
          }}
        />
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: isSelected ? "2px" : "1px",
            backgroundColor: band.color,
            opacity: isSelected ? 1 : 0.5
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: isSelected ? "2px" : "1px",
            backgroundColor: band.color,
            opacity: isSelected ? 1 : 0.5
          }}
        />
        {showLabel && (
          <div
            className="element-band-label"
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%) rotate(-90deg)",
              whiteSpace: "nowrap",
              fontSize: "9px",
              fontWeight: isSelected ? 800 : 700,
              color: band.color,
              letterSpacing: "0.04em",
              lineHeight: 1,
              pointerEvents: "none",
              userSelect: "none",
              textTransform: "uppercase",
              opacity: isSelected ? 1 : 0.8
            }}
          >
            {band.name === "Page Break" ? "PB" : band.name}
          </div>
        )}
      </div>
    );
  };

  handleBarClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  render() {
    const { height, zoomScale, selectedKuikaIds } = this.props;
    const { bands } = this.state;
    const scrollOffset = this.getActualScrollOffset();
    const scaledOffset = scrollOffset * zoomScale;
    const selectedSet = new Set(selectedKuikaIds || []);

    const pageBreakBands: IElementBand[] = (this.props.pageBreakPositions || []).map((yPos) => ({
      name: "Page Break",
      kuikaId: "",
      top: yPos,
      height: 20,
      color: "var(--red-5)"
    }));

    const fixedBandNames = new Set(["Header", "Footer", "Page Header", "Page Footer"]);
    const fixedBands = bands.filter((b) => fixedBandNames.has(b.name));
    const scrollableBands = [...bands.filter((b) => !fixedBandNames.has(b.name)), ...pageBreakBands];

    const hasFixed = fixedBands.length > 0;
    const hasScrollable = scrollableBands.length > 0;

    const hasAny = hasFixed || hasScrollable;
    const laneWidth = hasFixed && hasScrollable ? "14px" : "28px";

    return (
      <div
        className="element-bands-bar"
        onClick={this.handleBarClick}
        style={{
          width: "28px",
          height: `${height}px`,
          position: "relative",
          overflow: "hidden",
          flexShrink: 0,
          zIndex: 10,
          pointerEvents: hasAny ? "auto" : "none",
          display: "flex",
          flexDirection: "row"
        }}
      >
        {/* Fixed lane: Header/Footer */}
        {hasFixed && (
          <div style={{ width: laneWidth, height: "100%", position: "relative", overflow: "hidden" }}>
            {fixedBands.map((band, index) => {
              const scaledTop = band.top * zoomScale - scaledOffset;
              const scaledHeight = band.height * zoomScale;
              if (scaledTop + scaledHeight < 0 || scaledTop > height) return null;
              const isSelected = !!band.kuikaId && selectedSet.has(band.kuikaId);
              return this.renderBand(band, index, scaledTop, scaledHeight, isSelected);
            })}
          </div>
        )}
        {/* Scrollable lane: ReportTable/StaticTable/Page Break */}
        {hasScrollable && (
          <div style={{ width: laneWidth, height: "100%", position: "relative", overflow: "hidden" }}>
            {scrollableBands.map((band, index) => {
              const scaledTop = band.top * zoomScale - scaledOffset;
              const scaledHeight = band.height * zoomScale;
              if (scaledTop + scaledHeight < 0 || scaledTop > height) return null;
              const isSelected = !!band.kuikaId && selectedSet.has(band.kuikaId);
              return this.renderBand(band, index, scaledTop, scaledHeight, isSelected);
            })}
          </div>
        )}
      </div>
    );
  }
}
